<template>
  <v-dialog
    no-click-animation
    persistent
    content-class="dialog"
    max-width="1140px"
    v-model="dialog"
    fullscreen
    :retain-focus="false"
    hide-overlay
    dark
    transition="dialog-bottom-transition">
    <v-dialog v-model="showFreeTrainingWelcome" fullscreen hide-overlay persistent transition="fade-transition">
      <div class="welcome-overlay">
        <div class="welcome-content">
          <div class="emoji-line">💪</div>
          <h1 class="welcome-title">
            <span class="line1">Let's</span><br />
            <span class="line2">Go!</span>
          </h1>
          <p class="welcome-subtitle">Dein freies Training ist startklar. Füge jetzt deine erste Übung hinzu.</p>
          <v-btn x-large rounded depressed color="white" class="font-weight-bold black--text mt-8 start-btn" @click="openAddExerciseFromWelcome">
            <v-icon left>mdi-plus-circle</v-icon>
            Erste Übung auswählen
          </v-btn>
        </div>
      </div>
    </v-dialog>
    <div
      class="mainpage-top"
      :class="{ 'hyrox-mode-active': activeSplit && activeSplit.isHyrox }"
      :style="{
        height: overWrittenDivHeight + 'px',
        width: '100vw',
        maxWidth: '520px',
        background: getHeaderBackground(),
        boxShadow: isExpanded ? '0 8px 32px 0 rgba(31, 38, 135, 0.37)' : '0 4px 15px rgba(0,0,0,0.1)',
        backdropFilter: isExpanded ? 'blur(10px)' : 'none',
        borderBottomLeftRadius: isExpanded ? '24px' : '16px',
        borderBottomRightRadius: isExpanded ? '24px' : '16px',
      }">
      <v-row no-gutters align="center" class="px-2 py-1 header-row-base">
        <v-col cols="auto">
          <v-btn icon @click="onClose()" :ripple="false">
            <v-icon :color="getTextColorBasedOnBackground()">mdi-chevron-left</v-icon>
          </v-btn>
        </v-col>
        <v-col class="overflow-hidden">
          <div v-if="supersetIndicator" class="caption grey--text text--lighten-1" style="line-height: 1.2">
            {{ supersetIndicator }}
          </div>
          <v-card-title
            class="ml-2 font-weight-bold pa-0 text-truncate"
            :class="[getTextColorBasedOnBackgroundClass()]"
            style="font-size: 1.1rem; line-height: 1.4">
            {{ getSelectedExercise ? currentExerciseDisplayTitle : activeSplit.title }}
            {{ getSelectedExercise && getSelectedExercise.deviceShortForm ? "(" + getSelectedExercise.deviceShortForm + ")" : "" }}
          </v-card-title>
        </v-col>
        <v-col v-if="getSelectedExercise && !getSelectedExercise.isSuperset && !activeSplit.isHyrox" cols="2" class="text-right">
          <v-btn large v-if="getSelectedExercise" icon @click="openSwapDialog" title="Übung austauschen">
            <v-icon>mdi-swap-horizontal</v-icon>
          </v-btn>
        </v-col>
        <v-col v-else-if="!getSelectedExercise" cols="auto" class="d-flex flex-row justify-content-center">
          <v-btn color="white" icon v-if="false" @click="isHelpDialogVisible = true">
            <v-icon :color="getTextColorBasedOnBackground()">mdi-help-circle-outline</v-icon>
          </v-btn>
          <v-btn icon @click="handleOpenAddDialog" title="Übung hinzufügen">
            <v-icon :color="getTextColorBasedOnBackground()">mdi-plus</v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <div :class="{ 'header-content-wrapper': activeSplit && activeSplit.isHyrox }">
        <div
          v-if="activeSplit && activeSplit.isHyrox && !getSelectedExercise"
          class="race-clock-container"
          :class="{ 'animate-in': raceClockStarted }">
          <div class="race-clock-display">{{ raceClockDisplay }}</div>
        </div>
        <v-expand-transition v-else>
          <div v-if="!isExpanded && !getSelectedExercise" class="collapsed-metrics px-3 pb-2 pt-1">
            <v-row no-gutters align="center" justify="space-around">
              <v-col class="text-center metric-item">
                <v-icon :color="getTextColorBasedOnBackground()" class="pb-1">mdi-fire</v-icon>
                <div class="metric-value" :class="[getTextColorBasedOnBackgroundClass()]">{{ caloriesBurned }} kcal</div>
              </v-col>
              <v-divider vertical :dark="isBackgroundDark()" :light="!isBackgroundDark()"></v-divider>
              <v-col class="text-center metric-item">
                <v-icon :color="getTextColorBasedOnBackground()" class="pb-1">mdi-timer-sand</v-icon>
                <div class="metric-value" :class="[getTextColorBasedOnBackgroundClass()]">{{ workoutMinutes }} Min</div>
              </v-col>
              <v-divider vertical :dark="isBackgroundDark()" :light="!isBackgroundDark()"></v-divider>
              <v-col class="text-center metric-item">
                <v-icon :color="getTextColorBasedOnBackground()" class="pb-1">mdi-weight-lifter</v-icon>
                <div class="metric-value" :class="[getTextColorBasedOnBackgroundClass()]">{{ liftedWeight }} kg</div>
              </v-col>
            </v-row>
          </div>
        </v-expand-transition>
      </div>

      <v-expand-transition>
        <div v-if="isExpanded && !getSelectedExercise" class="expanded-content pt-2">
          <v-card-title class="pb-1 pt-0 text-h6 font-weight-bold justify-center" :class="[getTextColorBasedOnBackgroundClass()]">
            Dein Live Workout
          </v-card-title>
          <v-row no-gutters class="justify-space-around px-2 py-2">
            <v-col v-for="metric in expandedMetrics" :key="metric.title" cols="4" sm="3" class="px-1 mb-2">
              <v-card
                flat
                class="rounded-xl pa-2 text-center d-flex flex-column align-center justify-center metric-card-expanded"
                :style="{ backgroundColor: 'rgba(255, 255, 255, 0.18)', backdropFilter: 'blur(5px)' }"
                height="115px">
                <v-icon size="38px" :color="metric.iconColor" class="mb-1">{{ metric.icon }}</v-icon>
                <div>
                  <number class="text-h5 font-weight-bold white--text" :from="0" :to="metric.value" :duration="0.8" />
                  <span class="caption white--text">{{ metric.unit }}</span>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <div v-show="overWrittenDivHeight > 230">
            <v-card-title class="pb-0 pt-2 text-subtitle-1 font-weight-medium justify-center" :class="[getTextColorBasedOnBackgroundClass()]">
              Beanspruchte Muskeln
            </v-card-title>
            <v-col cols="12" class="px-1 pb-2">
              <plan-run-interactive-human
                :exercises="mappedExercises"
                :activeClasses="activeClasses"
                colsAlign="space-between"
                hideTagWindows
                cols="5"
                :background="getInteractiveHumanBackground()"
                borderRadius="16px"
                :textColor="getTextColorBasedOnBackground()" />
            </v-col>
          </div>
        </div>
      </v-expand-transition>

      <v-col
        v-if="!getSelectedExercise && !activeSplit.isHyrox"
        cols="12"
        class="drag-handle pa-0"
        style="position: absolute; bottom: 0px; left: 0; right: 0; z-index: 1001"
        @click="
          () => {
            expandedFix = !expandedFix
            divHeight = divHeight === maxHeight ? initialHeight : maxHeight
          }
        ">
        <div style="height: 24px; display: flex; align-items: center; justify-content: center">
          <div
            :style="{
              height: '5px',
              width: '50px',
              borderRadius: '3px',
              backgroundColor: getDragHandleColor(),
            }"></div>
        </div>
      </v-col>
    </div>

    <v-card class="fill-height workout-content-area" style="background: #1e1e1e">
      <run-workout-exercise-list :initialHeight="initialHeight" @open-add-dialog="handleOpenAddDialog" />
    </v-card>

    <ExerciseSelectionDialog
      :dialog.sync="selectionDialogActive"
      :mode="selectionDialogMode"
      :insertion-index="selectionDialogInsertionIndex"
      :exercise-to-swap="getSelectedExercise"
      :recommended-tags="swapRecommendedTags"
      @swap-selected="configureNewExercise($event.newExercise)"
      @add-selected="configureNewExercise($event.newExercise)" />
    <help-dialog :dialog.sync="isHelpDialogVisible" />

    <v-dialog v-model="configureDialogVisible" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card dark tile style="background: #2a2a2a">
        <v-card-text class="pa-4" style="overflow-y: auto; height: calc(100vh - 64px)">
          <workout-split-edit-exercise-single
            v-if="exerciseToConfigure"
            :exercise-prop="exerciseToConfigure"
            @savedExercise="finalizeAction"
            @cancel-edit="cancelConfiguration" />
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showHyroxStartDialog" fullscreen hide-overlay persistent transition="fade-transition">
      <div class="hyrox-start-overlay" :class="{ 'start-transition': raceClockStarting }">
        <div class="race-clock-placeholder">
          <div class="race-clock-display">{{ raceClockDisplay }}</div>
        </div>

        <div class="hyrox-content">
          <h1 class="hyrox-main-title">
            <span class="char">H</span><span class="char">Y</span><span class="char">R</span><span class="char">O</span><span class="char">X</span>
          </h1>
          <p class="hyrox-subtitle">THE RACE IS ON</p>
          <v-btn x-large rounded depressed color="white" class="font-weight-bold black--text mt-8 start-btn" @click="startTheRace">
            <v-icon left>mdi-timer-play-outline</v-icon>
            START
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </v-dialog>
</template>

<script>
import PlanRunInteractiveHuman from "../../PlanRunInteractiveHuman.vue"
import RunWorkoutExerciseList from "./runWorkoutExerciseList.vue"
// NEU: Importiere die Komponente zum Bearbeiten der Sätze
import WorkoutSplitEditExerciseSingle from "../../WorkoutSplitEditExerciseSingle.vue" // BITTE PFAD PRÜFEN & ANPASSEN
import ExerciseSelectionDialog from "./runWorkoutExerciseSelectionDialog.vue" // Pfad ggf. anpassen
import HelpDialog from "./helpDialog.vue" // Pfad ggf. anpassen

import { mapState, mapActions, mapGetters } from "vuex"

function getLuminance(hexColor) {
  hexColor = hexColor.replace("#", "")
  const r = parseInt(hexColor.substring(0, 2), 16)
  const g = parseInt(hexColor.substring(2, 4), 16)
  const b = parseInt(hexColor.substring(4, 6), 16)
  return 0.299 * r + 0.587 * g + 0.114 * b
}

export default {
  name: "Workingout",
  components: {
    HelpDialog, // HIER HINZUFÜGEN
    PlanRunInteractiveHuman,
    RunWorkoutExerciseList,
    ExerciseSelectionDialog,
    WorkoutSplitEditExerciseSingle, // NEU
  },
  data() {
    return {
      raceClockStartTime: null, // NEU: Für präzise Zeitmessung
      raceClockMilliseconds: 0, // NEU: Wir messen in Millisekunden
      raceClockInterval: null,
      raceClockSeconds: 0,
      raceClockStarted: false, // Steuert die Sichtbarkeit der finalen Uhr
      raceClockStarting: false,
      showHyroxStartDialog: false, // NEU
      showFreeTrainingWelcome: false, // NEU

      isHelpDialogVisible: false, // HIER HINZUFÜGEN

      swapRecommendedTags: [], // NEU: Hält die empfohlenen Tags für den Austausch
      // Umbenannt für Klarheit
      selectionDialogActive: false,
      configureDialogVisible: false,

      // NEU: Steuert den Modus des Dialogs
      selectionDialogMode: "swap",
      selectionDialogInsertionIndex: null,
      configureDialogVisible: false, // NEU: Steuert den Konfigurations-Dialog
      exerciseToConfigure: null, // NEU: Hält die ausgewählte, aber noch nicht konfigurierte Übung
      dialog: true,
      initialHeight: 120,
      expandedThreshold: 180,
      maxHeight: window.innerHeight * 0.75,
      divHeight: 120,
      startY: 0,
      dragging: false,
      timerId: null,
      now: this.$moment(),
      expandedFix: false,
    }
  },
  computed: {
    ...mapState("runWorkoutStore", ["workoutStartTime", "activeSplit", "selectedExerciseIndex", "completedSetsMap", "activeSupersetSubIndex"]),
    ...mapGetters("runWorkoutStore", ["getSelectedExercise", "getActiveSupersetSubExercise"]),
    // ... alle anderen computed properties bleiben unverändert
    isResumingWorkout() {
      return Object.values(this.completedSetsMap).some((sets) => sets.length > 0)
    },
    raceClockDisplay() {
      const totalSeconds = this.raceClockMilliseconds / 1000
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = Math.floor(totalSeconds % 60)
      const hundredths = Math.floor((this.raceClockMilliseconds % 1000) / 10) // Berechnet die Hundertstel

      return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}:${String(hundredths).padStart(2, "0")}`
    },
    supersetIndicator() {
      if (this.getSelectedExercise?.isSuperset) {
        const currentIndex = this.activeSupersetSubIndex + 1
        const total = this.getSelectedExercise.exercises.length
        return `Supersatz (${currentIndex}/${total})`
      }
      return ""
    },
    overWrittenDivHeight() {
      if (this.getSelectedExercise) return 60
      return Math.max(this.initialHeight, this.divHeight)
    },
    currentExerciseDisplayTitle() {
      if (!this.getSelectedExercise) {
        return this.activeSplit.title
      }
      if (this.getSelectedExercise.isSuperset) {
        // Wenn Supersatz, nimm den Titel der aktiven Sub-Übung
        return this.getActiveSupersetSubExercise?.title || "Supersatz"
      }
      // Ansonsten den normalen Titel
      return this.getSelectedExercise.title
    },
    workoutMinutes() {
      if (!this.workoutStartTime) return 0
      return this.now.diff(this.workoutStartTime, "minutes")
    },
    isExpanded() {
      return this.expandedFix
      return !this.getSelectedExercise && this.divHeight > this.expandedThreshold
    },
    mappedExercises() {
      if (!this.activeSplit || !this.activeSplit.exercises) return []
      return this.activeSplit.exercises.map((e) => ({
        title: e.title,
        device: e.deviceTitle,
        tags: e.tags,
        img: this.getImageForExercise(e),
      }))
    },
    activeClasses() {
      if (!this.activeSplit || !this.activeSplit.exercises) return []
      return this.activeSplit.exercises.reduce((acc, e) => {
        e.tags.forEach((t) => {
          if (!acc.includes(t.class)) acc.push(t.class)
        })
        return acc
      }, [])
    },
    caloriesBurned() {
      return Math.round(this.workoutMinutes * 7 * 10) / 10
    },
    // KORRIGIERT: Greift jetzt auf completedSetsMap zu.
    liftedWeight() {
      if (!this.completedSetsMap) return 0
      const allSets = Object.values(this.completedSetsMap).flat()
      const totalWeight = allSets.reduce((total, set) => {
        if (!Array.isArray(set) || set.some((s) => s.skipped)) {
          return total
        }
        const weight = set.find((item) => item.text === "Gewicht")?.value || 0
        const reps = set.find((item) => /Wiederholungen/.test(item.text))?.value || 0
        return total + Number(weight) * Number(reps)
      }, 0)
      return Math.round(totalWeight)
    },
    expandedMetrics() {
      return [
        { title: "Kalorien", value: this.caloriesBurned, unit: "kcal", icon: "mdi-fire", iconColor: "orange" },
        { title: "Zeit", value: this.workoutMinutes, unit: "Min", icon: "mdi-timer-sand", iconColor: "blue" },
        { title: "Gewicht", value: this.liftedWeight, unit: "kg", icon: "mdi-weight-lifter", iconColor: "green" },
      ]
    },
    currentBackgroundColorHex() {
      if (this.getSelectedExercise?.warmup && !this.getSelectedExercise?.sets?.length) return "#FFA500"
      if (this.getSelectedExercise) return "#292929"
      if (this.activeSplit?.fixedVisualization?.color) return this.activeSplit.fixedVisualization.color
      return "#1e1e1e" // Fallback
    },
  },
  methods: {
    ...mapActions("runWorkoutStore", ["resetWorkoutStartTime", "setSelectedExerciseIndex", "swapExerciseTemporarily", "addExerciseTemporarily"]),
    startTheRace() {
      this.raceClockStarting = true // Startet die Übergangs-Animation
      // Wir geben der Animation etwas mehr Zeit, um sauber auszusehen
      setTimeout(() => {
        this.showHyroxStartDialog = false
        this.raceClockStarted = true
        this.startRaceClock()
      }, 600) // Dauer auf 1.2s erhöht
    },
    startRaceClock() {
      if (this.raceClockInterval) clearInterval(this.raceClockInterval)

      // Die Uhr basiert jetzt IMMER auf der ursprünglichen Startzeit aus dem Store
      const initialStartTime = this.$moment(this.workoutStartTime).add("seconds", 3)
      const updateClock = () => {
        const now = this.$moment()
        const elapsed = now.diff(initialStartTime) // Differenz in Millisekunden
        this.raceClockMilliseconds = Math.max(0, elapsed)
      }

      updateClock() // Einmal sofort ausführen, um die korrekte Zeit sofort anzuzeigen
      this.raceClockInterval = setInterval(updateClock, 51) // Hochfrequentes Update für flüssige Hundertstel
    },
    openAddExerciseFromWelcome() {
      this.showFreeTrainingWelcome = false
      this.handleOpenAddDialog()
    },
    updateLiveActivity() {
      if (window.flutter_inappwebview) {
        // Die native Seite berechnet die Dauer selbst. Wir könnten hier andere Daten,
        // wie z.B. das aktuelle Gesamtgewicht, als Update senden.
        const payload = {
          status: "update",
          liftedWeight: this.liftedWeight, // Beispiel für ein Update-Datum
        }
        window.flutter_inappwebview.callHandler("workoutActivityHandler", payload)
        console.log("Live Activity Update-Befehl gesendet:", payload)
      }
    },
    getNativeAssetName(imagePath) {
      if (!imagePath) return "default_split_image" // Fallback-Bildname
      // Extrahiert 'mein_bild' aus 'pfad/zu/mein_bild.png'
      return imagePath.split("/").pop().split(".")[0]
    },
    // --- NEUE UND ANGEPASSTE METHODEN FÜR DEN SWAP-PROZESS ---
    openSwapDialog() {
      if (!this.getSelectedExercise) return
      const currentExerciseInPlan = this.activeSplit.exercises[this.selectedExerciseIndex]
      this.swapRecommendedTags = Array.isArray(currentExerciseInPlan.recommendedTags) ? currentExerciseInPlan.recommendedTags : []

      this.selectionDialogMode = "swap"
      this.selectionDialogActive = true
    },

    // Wird vom Kind-Component per Event aufgerufen
    handleOpenAddDialog() {
      this.selectionDialogMode = "add"
      this.selectionDialogInsertionIndex = this.activeSplit.exercises.length // Füge am Ende des Plans hinzu
      this.swapRecommendedTags = [] // Keine Empfehlungen beim Hinzufügen
      this.selectionDialogActive = true
    },

    configureNewExercise(newExercise) {
      this.selectionDialogActive = false
      this.exerciseToConfigure = JSON.parse(JSON.stringify(newExercise))
      this.configureDialogVisible = true
    },

    // Diese Methode schließt den gesamten Vorgang ab
    finalizeAction(configuredExercise) {
      if (this.selectionDialogMode === "swap") {
        if (this.selectedExerciseIndex === null) return
        this.swapExerciseTemporarily({
          exerciseIndex: this.selectedExerciseIndex,
          newExercise: configuredExercise,
        })
        this.$toast.success(`${configuredExercise.title} wurde erfolgreich ausgetauscht!`)
      } else {
        // mode === 'add'
        this.addExerciseTemporarily({
          newExercise: configuredExercise,
          atIndex: this.selectionDialogInsertionIndex,
        })
        this.$toast.success(`${configuredExercise.title} wurde dem Plan hinzugefügt!`)
      }
      this.cancelConfiguration()
    },

    cancelConfiguration() {
      this.configureDialogVisible = false
      this.exerciseToConfigure = null
      this.selectionDialogInsertionIndex = null
    },
    getHeaderBackground() {
      // KORRIGIERT: Fügt einen Check hinzu
      if (this.activeSplit?.isHyrox) {
        return "rgb(20, 20, 20)" // Ein sattes Schwarz/Dunkelgrau
      }
      if (!this.activeSplit) return "#1e1e1e" // Fallback-Farbe
      if (this.getSelectedExercise?.warmup && !this.getSelectedExercise?.sets?.length) return "orange"
      if (this.getSelectedExercise) return "rgb(41, 41, 41)"
      return this.activeSplit.fixedVisualization.color
    },
    isBackgroundDark(hexColor = this.currentBackgroundColorHex) {
      if (this.activeSplit?.isHyrox) {
        return true
      }
      const luminance = getLuminance(hexColor)
      return this.getSelectedExercise?.warmup || luminance < 128
    },
    getTextColorBasedOnBackground() {
      return this.isBackgroundDark() ? "white" : "black"
    },
    getTextColorBasedOnBackgroundClass() {
      return this.isBackgroundDark() ? "white--text" : "black--text"
    },
    getDragHandleColor() {
      return this.isBackgroundDark() ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.3)"
    },
    getInteractiveHumanBackground() {
      return this.isBackgroundDark() ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"
    },
    updateHeaderColorForFlutter() {
      const { r, g, b } = this.hexToRgb(this.currentBackgroundColorHex)
      let rgbString = `rgb(${r}, ${g}, ${b})`
      if (window.flutter_inappwebview) {
        window.flutter_inappwebview.callHandler("colorHandler", rgbString)
      }
    },
    async onClose() {
      if (this.getSelectedExercise) {
        this.setSelectedExerciseIndex(-1)
      } else {
        // NEU: Live Activity stoppen, wenn kein Exercise ausgewählt ist

        if (window.flutter_inappwebview) {
          try {
            const payload = {
              status: "update",
              // Zähler für abgeschlossene Übungen erhöhen
              exercisesCompleted: justCompletedIndex + 1,
              // Titel der NÄCHSTEN Übung senden
              nextExerciseName: this.activeSplit.exercises[justCompletedIndex + 1]?.title || "Workout abschliessen!",
            }
            window.flutter_inappwebview.callHandler("workoutActivityHandler", payload)
            console.log("Live Activity Update (Übung beendet):", payload)
          } catch (e) {
            console.error("Fehler beim Senden des Activity-Updates:", e)
          }
        }
        this.dialog = false
        this.$router.replace({ name: "Workouts_v2" }) // Ziel anpassen falls nötig
      }
    },
    startDragging(event) {
      this.startY = event.touches[0].clientY
      this.dragging = true
      window.addEventListener("touchmove", this.drag, { passive: false })
      window.addEventListener("touchend", this.stopDragging)
    },
    drag(event) {
      if (!this.dragging) return
      event.preventDefault()
      const touchY = event.touches[0].clientY
      const deltaY = this.startY - touchY
      this.startY = touchY
      let newHeight = this.divHeight - deltaY
      this.divHeight = Math.max(this.initialHeight, Math.min(this.maxHeight, newHeight))
    },
    stopDragging() {
      this.dragging = false
      window.removeEventListener("touchmove", this.drag)
      window.removeEventListener("touchend", this.stopDragging)
      if (!this.getSelectedExercise) {
        if (this.divHeight < (this.maxHeight + this.initialHeight) / 2) {
          this.divHeight = this.initialHeight
        } else {
          this.divHeight = this.maxHeight
        }
      }
    },
    hexToRgb(hex) {
      hex = hex.replace(/^#/, "")
      if (hex.length === 3)
        hex = hex
          .split("")
          .map((ch) => ch + ch)
          .join("")
      if (hex.length !== 6) return { r: 0, g: 0, b: 0 }
      const bigint = parseInt(hex, 16)
      const r = (bigint >> 16) & 255
      const g = (bigint >> 8) & 255
      const b = bigint & 255
      return { r, g, b }
    },
    getImageForExercise(exercise) {
      if (exercise.picture) return this.getImageUrl(exercise.picture)
      if (exercise.manual && exercise.manual[0]?.picture) return this.getImageUrl(exercise.manual[0].picture)
      return "/img/einmalig/imageUploadPlaceholder.jpg"
    },
    getImageUrl(imageId) {
      if (!imageId) return ""
      return `/img/uebungen/${imageId.replace(/\.[^.]+$/, "")}.webp`
    },
  },
  created() {
    this.updateHeaderColorForFlutter()
    if (!this.workoutStartTime || this.$moment().diff(this.workoutStartTime, "hours") > 5) {
      this.resetWorkoutStartTime()
    }
    this.divHeight = this.initialHeight
  },
  // In Workingout.vue -> mounted()
  async mounted() {
    console.log(this.activeSplit, "teeest")
    //console.log(this.getNativeAssetName(this.activeSplit.fixedVisualization.img))
    this.timerId = setInterval(() => {
      this.now = this.$moment()
      // Wir rufen hier die Update-Methode auf
      this.updateLiveActivity()
    }, 15000) // Update alle 15 Sekunden ist ein guter Kompromiss

    // --- START: NEUE LOGIK ---
    if (this.activeSplit) {
      const titleLower = this.activeSplit.title.toLowerCase()
      if (titleLower.includes("hyrox")) {
        // Speziell für Hyrox den neuen Dialog anzeigen
        if (this.isResumingWorkout) {
          this.raceClockStarted = true
          this.startRaceClock()
        } else {
          this.showHyroxStartDialog = true
        }
      } else if (this.activeSplit.isFreeTraining) {
        // Den alten Dialog nur noch für NICHT-Hyrox freie Trainings anzeigen
        this.showFreeTrainingWelcome = true
      }
    }

    this.maxHeight = window.innerHeight * 0.75

    // NEU: Live Activity starten
    if (window.flutter_inappwebview) {
      try {
        const payload = {
          status: "start",
          splitTitle: this.activeSplit.title,
          splitColor: this.activeSplit.fixedVisualization.color,
          splitImageName: this.getNativeAssetName(this.activeSplit.fixedVisualization.img),
          startTimeISO: this.workoutStartTime,
          // NEU: Gesamtzahl der Übungen statt Sätze
          totalExercises: this.activeSplit.exercises.length,
          // NEU: Startwert für abgeschlossene Übungen
          exercisesCompleted: 0,
          nextExerciseName: this.activeSplit.exercises[0]?.title || "Workout starten",
        }
        await window.flutter_inappwebview.callHandler("workoutActivityHandler", payload)
        console.log("Live Activity Start-Befehl gesendet:", payload)
      } catch (e) {
        console.error("Fehler beim Starten der Live Activity:", e)
      }
    }
  },
  beforeDestroy() {
    clearInterval(this.timerId)
    clearInterval(this.raceClockInterval) // NEU
    window.removeEventListener("touchmove", this.drag)
    window.removeEventListener("touchend", this.stopDragging)
  },
  watch: {
    getSelectedExercise(newValue) {
      this.updateHeaderColorForFlutter()
      this.divHeight = newValue ? 60 : this.initialHeight
    },
    currentBackgroundColorHex() {
      this.updateHeaderColorForFlutter()
    },
  },
}
</script>
<style lang="scss" scoped>
.mainpage-top {
  position: fixed;
  overflow: hidden;
  margin: 0 auto;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(28, 28, 30, 0.65);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.header-row-base {
  height: 50px;
  flex-wrap: nowrap;
}
.collapsed-metrics .metric-item {
  padding: 0 4px;
}
.metric-card-expanded {
  background-color: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.metric-card-expanded:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}
.drag-handle {
  cursor: ns-resize;
}
.workout-content-area {
  background-color: #121212;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  overflow-y: auto;
  box-shadow: 0 -4px 10px rgba(0, 0, 0, 0.05);
}

/* --- START: VERBESSERTES "WOW-FAKTOR" STYLING --- */

.welcome-overlay {
  background: radial-gradient(circle at center, #2a2a2a, #121212);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  padding: 24px;
  height: 100vh;
  overflow: hidden;
  /* NEU: Der ganze Hintergrund blendet sanft ein */
  animation: overlay-fade-in 0.5s ease-out forwards;
}

.welcome-content > * {
  /* Stellt sicher, dass alle Kind-Elemente unsichtbar starten */
  opacity: 0;
}

.emoji-line {
  font-size: 5rem;
  text-shadow: 0 0 40px rgba(255, 255, 255, 0.7);
  animation: pop-in 0.8s 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
}

.welcome-title {
  font-size: 6rem;
  font-weight: 900;
  line-height: 1;
  text-shadow: 0 0 15px rgba(255, 255, 255, 0.2), 0 0 40px rgba(255, 255, 255, 0.5);
  letter-spacing: -0.05em;
  /* Wichtig, damit die Kind-Animationen funktionieren */
  animation: fade-in 0.1s 0.6s forwards;
}

.welcome-title span {
  display: inline-block;
  opacity: 0;
  transform: translateY(60px) rotate(8deg);
  /* NEU: Weichere und deutlichere Animation */
  animation: reveal-text 0.8s cubic-bezier(0.19, 1, 0.22, 1) forwards;
}

.welcome-title .line1 {
  animation-delay: 0.8s;
}
.welcome-title .line2 {
  animation-delay: 0.9s;
}

.welcome-subtitle {
  font-size: 1.1rem;
  max-width: 350px;
  margin: 24px auto 0;
  color: #b0b0b0;
  animation: fade-in-up 0.8s 1.2s cubic-bezier(0.19, 1, 0.22, 1) forwards;
}

.start-btn {
  animation: fade-in-up 0.8s 1.4s cubic-bezier(0.19, 1, 0.22, 1) forwards, pulse-shadow 2.5s 2s infinite ease-in-out;
  box-shadow: 0 0 25px rgba(var(--v-primary-base-rgb), 0.4);
}

/* --- KEYFRAME ANIMATIONEN FÜR DEN "WOW-FAKTOR" --- */

@keyframes overlay-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pop-in {
  from {
    opacity: 0;
    transform: scale(0.5);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes reveal-text {
  from {
    opacity: 0;
    transform: translateY(60px) rotate(8deg);
  }
  to {
    opacity: 1;
    transform: translateY(0) rotate(0deg);
  }
}

@keyframes pulse-shadow {
  0% {
    box-shadow: 0 0 25px rgba(var(--v-primary-base-rgb), 0.4);
  }
  50% {
    box-shadow: 0 0 40px 8px rgba(var(--v-primary-base-rgb), 0.6);
  }
  100% {
    box-shadow: 0 0 25px rgba(var(--v-primary-base-rgb), 0.4);
  }
}

/* In runWorkout.vue -> <style> */

/* --- START: EPISCHES STYLING FÜR HYROX-START --- */

.hyrox-start-overlay {
  background-color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  height: 100vh;
  overflow: hidden;
  position: relative;
}

.hyrox-start-overlay::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 150%;
  padding-bottom: 150%;
  background: radial-gradient(circle, rgba(255, 237, 0, 0.2) 0%, transparent 50%);
  transform-origin: center;
  transform: translate(-50%, -50%) scale(0);
  animation: background-pulse 2s 0.5s forwards;
}

.hyrox-content {
  position: relative;
  z-index: 2;
}

.hyrox-main-title {
  font-size: 10rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.1em;
  color: #fff;
  text-shadow: 0 0 15px rgba(255, 255, 255, 0.5), 0 0 30px #ffed00;
}

.hyrox-main-title .char {
  display: inline-block;
  opacity: 0;
  transform: translateY(-80px) scale(0.5);
  animation: letter-drop 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
}

/* Staggered animation für die Buchstaben */
.hyrox-main-title .char:nth-child(1) {
  animation-delay: 0.8s;
}
.hyrox-main-title .char:nth-child(2) {
  animation-delay: 0.9s;
}
.hyrox-main-title .char:nth-child(3) {
  animation-delay: 1s;
}
.hyrox-main-title .char:nth-child(4) {
  animation-delay: 1.1s;
}
.hyrox-main-title .char:nth-child(5) {
  animation-delay: 1.2s;
}

.hyrox-subtitle {
  font-size: 1.5rem;
  font-weight: 500;
  letter-spacing: 0.3em;
  color: #ffed00;
  text-transform: uppercase;
  margin-top: 1rem;
  opacity: 0;
  animation: fade-in-up 1s 1.5s forwards;
}

.hyrox-content .start-btn {
  opacity: 0;
  animation: fade-in-up 1s 1.8s forwards;
}

@keyframes background-pulse {
  from {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0;
  }
  to {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
}

@keyframes letter-drop {
  from {
    opacity: 0;
    transform: translateY(-80px) scale(0.5) rotate(-45deg);
    filter: blur(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1) rotate(0deg);
    filter: blur(0);
  }
}

.mainpage-top.hyrox-mode-active {
  background: #141414 !important;
  display: flex;
  flex-direction: column;
}
.header-content-wrapper {
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  bottom: 8px;
}
.race-clock-container {
  opacity: 0;
  transform: translateY(10px);
  transition: opacity 0.4s 0.2s ease, transform 0.4s 0.2s ease;
}
.race-clock-container.animate-in {
  opacity: 1;
  transform: translateY(0);
}
.race-clock-display {
  font-family: "Teko", sans-serif;
  font-size: 3.5rem;
  font-weight: 700;
  color: #ffed00;
  text-shadow: 0 0 10px rgba(255, 237, 0, 0.7);
  line-height: 1;
}

/* --- NEUE LOGIK FÜR DEN ÜBERGANG --- */

/* Die Platzhalter-Uhr, die genau dort sitzt, wo die echte Uhr sein wird */
.race-clock-placeholder {
  position: absolute;
  top: 60px; /* Höhe des Titel-Headers */
  left: 0;
  right: 0;
  height: 120px; /* Höhe des gesamten Headers */
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0; /* Startet unsichtbar */
}
.hyrox-start-overlay.start-transition .race-clock-placeholder {
  animation: fade-in 0.5s 0.7s forwards; /* Blendet sich am Ende ein */
}

/* Der Titel wird jetzt einfach kleiner und blendet aus */
.hyrox-start-overlay.start-transition .hyrox-main-title {
  animation: scale-and-fade-out 0.8s cubic-bezier(0.5, 0, 0.2, 1) forwards;
}

/* Das Overlay selbst blendet am Ende aus */
.hyrox-start-overlay.start-transition {
  animation: fade-out-bg 0.4s 0.8s forwards;
}

/* Die anderen Elemente blenden früher aus */
.hyrox-start-overlay.start-transition .hyrox-subtitle,
.hyrox-start-overlay.start-transition .start-btn {
  animation: fade-out-down 0.3s ease-out forwards;
}

/* NEUE KEYFRAMES */
@keyframes scale-and-fade-out {
  from {
    transform: scale(1);
    opacity: 1;
  }
  to {
    transform: scale(0.2);
    opacity: 0;
  }
}
@keyframes fade-out-bg {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes fade-out-down {
  to {
    opacity: 0;
    transform: translateY(50px);
  }
}
</style>
